import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Group, PuroReservation, ValamarBooking } from '../models';
import type { Sunbed, SunbedTypeInfo } from '@models/sunbed';
import { BACKEND_PRODUCT_MAP, GuestPackageType, Prices, ProductType } from '@app/utils';

/**
 * Application state management service using BehaviorSubject pattern
 * Manages the complete check-in flow state
 */
@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  // Current step in the check-in process
  private currentStepSubject: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  public currentStep$: Observable<number> = this.currentStepSubject.asObservable();

  // Current reservation being processed
  private reservationSubject: BehaviorSubject<PuroReservation | null> = new BehaviorSubject<PuroReservation | null>(null);
  public reservation$: Observable<PuroReservation | null> = this.reservationSubject.asObservable();

  // Valamar guest verification
  private isValamarGuestSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isValamarGuest$: Observable<boolean> = this.isValamarGuestSubject.asObservable();

  // Selected Valamar booking
  private valamarBookingSubject: BehaviorSubject<ValamarBooking | null> = new BehaviorSubject<ValamarBooking | null>(null);
  public valamarBooking$: Observable<ValamarBooking | null> = this.valamarBookingSubject.asObservable();

  // Groups for credit assignment
  private groupsSubject: BehaviorSubject<Group[]> = new BehaviorSubject<Group[]>([]);
  public groups$: Observable<Group[]> = this.groupsSubject.asObservable();

  // Submit status
  private isSubmittedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isSubmitted$: Observable<boolean> = this.isSubmittedSubject.asObservable();

  // Whether complete-daily-checkin has already succeeded for the current groups
  // (prevents re-submitting/duplicating gastro master cards if the user navigates back to /credit)
  private dailyCheckinCompletedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public dailyCheckinCompleted$: Observable<boolean> = this.dailyCheckinCompletedSubject.asObservable();
  sunbeds: Sunbed[] = [];

  // Daily budget
  private dailyBudgetSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public dailyBudget$: Observable<number> = this.dailyBudgetSubject.asObservable();

  // Manual overdraft
  private manualOverdraftSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public manualOverdraft$: Observable<number> = this.manualOverdraftSubject.asObservable();

  // Selected date (for viewing past transfers)
  private selectedDateSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public selectedDate$: Observable<string> = this.selectedDateSubject.asObservable();

  // V-Level override (allows operator to grant V-Level status to Collection guests)
  private guestPackageSubject: BehaviorSubject<GuestPackageType> = new BehaviorSubject<GuestPackageType>(GuestPackageType.EXTERNAL);
  public guestPackage$: Observable<GuestPackageType> = this.guestPackageSubject.asObservable();

  // Walk-in reservation flag
  public _isWalkIn: boolean = false;

  // Getters for current values
  get currentStep(): number {
    return this.currentStepSubject.value;
  }

  get reservation(): PuroReservation | null {
    return this.reservationSubject.value;
  }

  get isValamarGuest(): boolean {
    return this.isValamarGuestSubject.value;
  }

  get valamarBooking(): ValamarBooking | null {
    return this.valamarBookingSubject.value;
  }

  get groups(): Group[] {
    return this.groupsSubject.value;
  }

  get isSubmitted(): boolean {
    return this.isSubmittedSubject.value;
  }

  get dailyCheckinCompleted(): boolean {
    return this.dailyCheckinCompletedSubject.value;
  }

  get dailyBudget(): number {
    return this.dailyBudgetSubject.value;
  }

  get manualOverdraft(): number {
    return this.manualOverdraftSubject.value;
  }

  get selectedDate(): string {
    return this.selectedDateSubject.value;
  }

  get guestPackage(): GuestPackageType {
    return this.guestPackageSubject.value;
  }

  // Setters / Update methods
  setCurrentStep(step: number): void {
    this.currentStepSubject.next(step);
  }

  setReservation(reservation: PuroReservation | null): void {
    this.reservationSubject.next(reservation);
  }

  setIsValamarGuest(isValamar: boolean): void {
    this.isValamarGuestSubject.next(isValamar);
  }

  setValamarBooking(booking: ValamarBooking | null): void {
    this.valamarBookingSubject.next(booking);
  }

  setGroups(groups: Group[]): void {
    this.groupsSubject.next(groups);
  }

  setIsSubmitted(submitted: boolean): void {
    this.isSubmittedSubject.next(submitted);
  }

  setDailyCheckinCompleted(completed: boolean): void {
    this.dailyCheckinCompletedSubject.next(completed);
  }

  setDailyBudget(budget: number): void {
    this.dailyBudgetSubject.next(budget);
  }

  setManualOverdraft(overdraft: number): void {
    this.manualOverdraftSubject.next(overdraft);
  }

  setSelectedDate(date: string): void {
   const today = new Date();
    this.selectedDateSubject.next(this.parseDate(date) || today.toDateString());
  }

  // Group management methods

  addGroup(group: Group): void {
    const currentGroups = this.groupsSubject.value;
    this.groupsSubject.next([...currentGroups, group]);
  }

  updateGroup(groupId: string, updates: Partial<Group>): void {
    const currentGroups = this.groupsSubject.value;
    const updatedGroups = currentGroups.map(group =>
      group.id === groupId ? { ...group, ...updates } : group
    );
    this.groupsSubject.next(updatedGroups);
  }

  removeGroup(groupId: string): void {
    const currentGroups = this.groupsSubject.value;
    this.groupsSubject.next(currentGroups.filter(group => group.id !== groupId));
  }

  // Reset methods

  resetToStep1(): void {
    this.currentStepSubject.next(1);
    this.reservationSubject.next(null);
    this.isValamarGuestSubject.next(false);
    this.valamarBookingSubject.next(null);
    this.guestPackageSubject.next(GuestPackageType.EXTERNAL);
    this.groupsSubject.next([]);
    this.isSubmittedSubject.next(false);
    this.dailyCheckinCompletedSubject.next(false);
    this.dailyBudgetSubject.next(0);
    this.manualOverdraftSubject.next(0);
    this.selectedDateSubject.next('');
    this.sunbeds = [];
    this._isWalkIn = false;
    //todo:: ako odaberemo neki history datum ,pregledamo ,idemo nazad pa kliknemo nastavi ,ostane nam ovaj history datum ..treba ga ocistiti
  }

  resetAll(): void {
    this.resetToStep1();
  }

  // Helper methods
  getUnassignedSunbeds(): Sunbed[] {
    return this.sunbeds.filter(c => !c.assigned);
  }

  assignSunbedToGroup(sunbedId: string, groupId: string): void {
    const sunbed = this.sunbeds.find(c => c.id === sunbedId);
    if (!sunbed) return;

    if (sunbed.assigned) return;

    const currentGroups = this.groupsSubject.value;
    const groupIndex = currentGroups.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return;

    sunbed.assigned = true;
    sunbed.groupId = groupId;

    const alreadyExists = currentGroups[groupIndex].sunbeds.some(c => c.id === sunbed.id);

    if (!alreadyExists) {
      const updatedGroups = [...currentGroups];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        sunbeds: [...updatedGroups[groupIndex].sunbeds, sunbed],
        price: Number((updatedGroups[groupIndex].price + (sunbed.price || 0)).toFixed(2))
      };
      this.groupsSubject.next(updatedGroups);
    }
  }

  removeSunbedFromGroup(sunbedId: string, groupId: string): void {
    const currentGroups = this.groupsSubject.value;
    const groupIndex = currentGroups.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return;

    const removedSunbed = currentGroups[groupIndex].sunbeds.find(c => c.id === sunbedId);
    const removedSunbedPrice = removedSunbed?.price || 0;

    const updatedGroups = [...currentGroups];
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      sunbeds: updatedGroups[groupIndex].sunbeds.filter(c => c.id !== sunbedId),
      price: Number((updatedGroups[groupIndex].price - removedSunbedPrice).toFixed(2))
    };
    this.groupsSubject.next(updatedGroups);

    const sunbed = this.sunbeds.find(c => c.id === sunbedId);
    if (sunbed) {
      sunbed.assigned = false;
      sunbed.groupId = undefined;
      sunbed.number = "";
    }
  }

  // ============================================================================
  // SUNBED INITIALIZATION
  // ============================================================================

  /**
   * Initialize sunbeds for the current reservation and selected date.
   * This method acts as an orchestrator, delegating to helper methods.
   */
  initializeSunbeds(): void {
    // No reservation to process
    if (!this.reservation) return;
    // Determine the guest type (EXTERNAL, COLLECTION, or VLEVEL)
    const guestType = this.getGuestType();
    // Get sunbed items for the selected date (from roomList or chairPrices)
    const sunbedItems = this.getSunbedItemsForSelectedDate();

    // Create all sunbeds by flattening the array of arrays
    this.sunbeds = sunbedItems.flatMap(item =>
      this.createSunbedsForItem(item, guestType)
    );
    console.log("sunbedItems ",sunbedItems)
    console.log("sunbeds ",this.sunbeds)

  }

  /**
   * Determine the guest type based on Valamar status and V-Level.
   */
  private getGuestType(): GuestPackageType {
  return this.guestPackage
  }
  /**
   * Normalize a date to midnight (00:00:00.000) for consistent comparison.
   */
  private normalizeDate(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  /**
   * Get sunbed items for the currently selected date.
   * Uses roomList if available, otherwise falls back to chairPrices.
   */
  private getSunbedItemsForSelectedDate(): SunbedTypeInfo[] {
    const reservation = this.reservation;
    if (!reservation) return [];

    // Use roomList if available
    if (reservation.roomList && reservation.roomList.length > 0) {
      const selectedDate = this.selectedDate && this.selectedDate.trim() !== ''
        ? this.normalizeDate(new Date(this.selectedDate))
        : this.normalizeDate(new Date());

      return reservation.roomList
        .filter(room => {
          // Skip rooms without startDate
          if (!room.startDate) return false;

          const roomDate = this.normalizeDate(new Date(room.startDate));
          return roomDate.getTime() === selectedDate.getTime();
        })
        .map(room => ({
          type: room.roomName,
          price: room.price ?? 0,
          quantity: room.quantity
        }));
    }

    // Fallback to chairPrices
    return reservation.chairPrices.map(cp => ({
      type: cp.sunbed,
      price: cp.price,
      quantity: 1
    }));
  }

  /**
   * Create multiple sunbed instances for a given item type and quantity.
   */
  private createSunbedsForItem(
    item: SunbedTypeInfo,
    guestType: GuestPackageType
  ): Sunbed[] {
    // Map backend sunbed name to ProductType enum
    const productType = this.getProductType(item.type);
    console.log("Item->",item)
    console.log("Product->",productType)
    console.log("guest type", guestType);

    let credit = 0;

    if (productType) {
      // Use Prices.ts as the single source of truth
      console.log("product >",Prices.getProductDataForType(productType));
      credit = Prices.getPriceForProductType(productType, guestType);
      console.log("Credit type", credit);
    } else {
      // Unknown sunbed type - log warning and set credit to 0
      console.warn(`Nepoznat tip ležaljke: "${item.type}"`);
    }

    // Create the specified quantity of sunbeds
    return Array.from({ length: item.quantity }, () =>
      this.createSunbed(item.type, item.price, credit)
    );
  }

  /**
   * Create a single sunbed object.
   */
  private createSunbed(
    type: string,
    price: number,
    credit: number
  ): Sunbed {
    const priceWithDiscount = Number((price - credit).toFixed(2));

    return {
      id: this.generateCardId(),
      type,
      price,
      priceWithDiscount,
      credit,
      assigned: false,
      number: '',
      groupId: undefined
    };
  }
  private getProductType(backendName: string): ProductType | null {
    const normalized = backendName
      .trim()
      .toLowerCase();

    return BACKEND_PRODUCT_MAP[normalized] ?? null;
  }
   generateCardId() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

    let result = 'M';

    for (let i = 0; i < 15; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }
  parseDate(date?:string) {
    if (!date) {
      return undefined;
    }

    return new Date(date).toISOString().split('T')[0];
  }

   setGuestPackage(newPackage: GuestPackageType) {
    this.guestPackageSubject.next(newPackage);
  }

  // Walk-in: no Opera connection AND no online payment
  get isWalkIn(): boolean {
    return this._isWalkIn;
  }

  setIsWalkIn(value: boolean): void {
    this._isWalkIn = value;
  }
}
